import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, fetchClosePortfolio, fetchAssetRegister } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] outline-none focus:border-[var(--gold)] mb-2";
const money = (v) => {
  if (v == null) return null;
  const s = String(v);
  const neg = s.startsWith("-");
  const [intPart, decPart = ""] = (neg ? s.slice(1) : s).split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = decPart ? `${grouped}.${decPart}` : grouped;
  return neg ? `($${body})` : `$${body}`;
};
const hz = (s) => (s == null ? "" : String(s).replace(/_/g, " "));
const errMsg = (d, f) => (typeof d === "string" ? d : d?.message || d?.detail || f || "Something went wrong.");

const Unknown = ({ testid }) => <span className="italic text-[#e7a877]" data-testid={testid}>UNKNOWN</span>;
const Nbv = ({ nbv }) => (nbv?.status === "COMPUTED" ? <span>{money(nbv.net_book_value)}</span> : <Unknown />);

const OWN_TONE = {
  VERIFIED: "text-[#8fd19e] border-[#8fd19e]/40", FOUNDER_SUPPLIED: "text-[#8fd19e] border-[#8fd19e]/40",
  PROPOSED: "text-[#e7a877] border-[#e7a877]/40", EVIDENCE_PENDING: "text-[#e7a877] border-[#e7a877]/40",
  UNKNOWN: "text-[#e88f6a] border-[#e88f6a]/40", DISPOSED: "text-[var(--muted)] border-[var(--muted)]/40",
};
const Badge = ({ text, cls, testid }) => (
  <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-wide2 ${cls || "text-[var(--bone)] border-[rgba(180,139,63,0.45)]"}`} title={text} data-testid={testid}>{hz(text)}</span>
);
const Metric = ({ label, children, testid }) => (
  <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid={testid}>
    <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold)]/70">{label}</p>
    <div className="mt-1 text-lg text-[var(--bone)] font-serif2">{children}</div>
  </div>
);

function Login({ onIn }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false); const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr(null);
    try { await adminLogin(email, pw); onIn(); }
    catch (e) { setErr(errMsg(e.response?.data?.detail, "Login failed.")); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="assets-login-form">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl mb-4">Founder Asset Register</h1>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="assets-login-email" aria-label="Email" />
        <input className={inputCls} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="assets-login-password" aria-label="Password" />
        <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="assets-login-submit">{loading ? "…" : "Enter"}</button>
        {err && <p className="mt-3 text-sm text-[#e88f6a]" data-testid="assets-login-error">{err}</p>}
      </form>
    </div>
  );
}

function AttentionList({ title, items, testid, render }) {
  return (
    <section className="mb-5" data-testid={testid}>
      <h3 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">{title} · {items.length}</h3>
      {items.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid={`${testid}-empty`}>None.</p>
        : <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]">
            {items.map((it, i) => <li key={i} className="px-4 py-2 text-sm text-[#e7a877]">{render(it)}</li>)}
          </ul>}
    </section>
  );
}

function Console({ onSignOut }) {
  const [entities, setEntities] = useState([]);
  const [eid, setEid] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetchClosePortfolio().then((r) => {
      const list = (r.entities || []).map((e) => ({ id: e.entity_id, name: e.entity_name || e.legal_name || e.name || e.entity_id }));
      setEntities(list); if (!list.length) setErr("This workspace has no financial entities supported by verified source records. Establish those records before generating the calculations presented in this view."); if (list[0]) setEid(list[0].id);
    }).catch((e) => setErr(errMsg(e.response?.data?.detail, "Could not load entities.")));
  }, []);

  const load = useCallback(() => {
    if (!eid) return;
    setD(null); setErr(null);
    fetchAssetRegister(eid).then(setD).catch((e) => setErr(errMsg(e.response?.data?.detail, "Asset register unavailable.")));
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  const att = d?.attention || {};

  return (
    <div className="min-h-screen bg-[var(--navy)] text-[var(--bone)]">
      <header className="border-b border-[rgba(180,139,63,0.2)] px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-lg">Founder Asset Register</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs px-2 py-1" data-testid="assets-entity-select" aria-label="Entity" value={eid} onChange={(e) => setEid(e.target.value)}>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.id.slice(0, 6)}</option>)}
          </select>
          <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="assets-signout">Sign out</button>
        </div>
      </header>

      <main className="px-4 md:px-8 py-6 max-w-6xl">
        {err ? <p className="text-sm text-[#e88f6a]" data-testid="assets-error">{err}</p>
        : !d ? <p className="text-sm text-[var(--muted)]" data-testid="assets-loading">Loading asset register…</p>
        : (
          <div data-testid="assets-view">
            <p className="text-sm text-[var(--muted)] mb-1">{d.entity_name} · <span data-testid="assets-consolidation">{d.consolidation_status}</span> · Tax: <span data-testid="assets-tax-status" title={d.tax_treatment_status}>{hz(d.tax_treatment_status)}</span></p>
            <p className="text-[11px] text-[var(--muted)] mb-5">{d.authority_note}</p>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Metric label="Total records" testid="metric-total">{d.counts.total}</Metric>
              <Metric label="Owned net book value" testid="metric-nbv">
                {d.owned_net_book_value.status === "COMPUTED" ? money(d.owned_net_book_value.value) : <Unknown testid="nbv-unknown" />}
              </Metric>
              <Metric label="Ledger reconciliation" testid="metric-recon">
                {d.reconciliation.status === "COMPUTED"
                  ? <span data-testid="recon-result" className={d.reconciliation.matched ? "text-[#8fd19e]" : "text-[#e88f6a]"}>{d.reconciliation.matched ? "Matched" : "Mismatch"}</span>
                  : <Unknown testid="recon-unknown" />}
              </Metric>
              <Metric label="Cross-entity transfer" testid="metric-xfer">{hz(d.cross_entity_transfer_status)}</Metric>
            </section>

            <section className="mb-6" aria-labelledby="reg-h">
              <h2 id="reg-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Asset Register</h2>
              {d.assets.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid="register-empty">No asset records for this entity.</p>
              : <div className="overflow-x-auto"><table className="w-full text-sm border border-[rgba(180,139,63,0.18)]" data-testid="register-table">
                  <thead><tr className="text-left text-[var(--muted)] text-xs uppercase tracking-wide2">
                    <th className="px-3 py-2">Class</th><th className="px-3 py-2">Ownership</th><th className="px-3 py-2">Operational</th>
                    <th className="px-3 py-2">Location</th><th className="px-3 py-2">Book basis</th><th className="px-3 py-2">Net book value</th></tr></thead>
                  <tbody>
                    {d.assets.map((a) => (
                      <tr key={a.asset_id} className="border-t border-[rgba(180,139,63,0.12)]" data-testid={`asset-row-${a.asset_id}`}>
                        <td className="px-3 py-2">{a.asset_class}{a.is_candidate && <span className="ml-2 text-[10px] text-[#e88f6a]" data-testid={`candidate-flag-${a.asset_id}`}>CANDIDATE — NOT OWNED</span>}</td>
                        <td className="px-3 py-2"><Badge text={a.ownership_state} cls={OWN_TONE[a.ownership_state]} testid={`ownership-${a.asset_id}`} /></td>
                        <td className="px-3 py-2"><Badge text={a.lifecycle_state} testid={`lifecycle-${a.asset_id}`} /></td>
                        <td className="px-3 py-2">{a.location_ref || <span className="text-[var(--muted)]">—</span>}</td>
                        <td className="px-3 py-2">{a.accounting_book_basis != null ? money(a.accounting_book_basis) : <Unknown />}</td>
                        <td className="px-3 py-2" data-testid={`nbv-${a.asset_id}`}><Nbv nbv={a.net_book_value} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>}
            </section>

            <section aria-labelledby="attn-h">
              <h2 id="attn-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-3">Needs Your Attention</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <AttentionList title="Missing evidence" items={att.missing_evidence || []} testid="attn-missing-evidence" render={(id) => id} />
                <AttentionList title="Unknown useful life / treatment" items={att.unknown_useful_life_or_treatment || []} testid="attn-unknown-life" render={(id) => id} />
                <AttentionList title="Construction in progress" items={att.construction_in_progress || []} testid="attn-cip" render={(c) => `${c.title} · ${c.accumulated_cost != null ? money(c.accumulated_cost) : "UNKNOWN"}`} />
                <AttentionList title="Impairment review" items={att.impairment_review || []} testid="attn-impairment" render={(id) => id} />
                <AttentionList title="Proposed disposals" items={att.proposed_disposals || []} testid="attn-proposed-disposals" render={(id) => id} />
                <AttentionList title="Completed disposals" items={att.completed_disposals || []} testid="attn-completed-disposals" render={(c) => `${c.asset_id} · gain/loss ${c.gain_loss?.status === "COMPUTED" ? money(c.gain_loss.gain_loss) : "UNKNOWN"}`} />
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

export default function FounderAssetRegister() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
